import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { navLinks } from "@/mocks/site";

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const isHome = location.pathname === "/";
  const overHero = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const isActive = (path: string) => {
    const base = path.split("?")[0].split("#")[0];
    if (base === "/") return location.pathname === "/";
    return location.pathname.startsWith(base);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        overHero
          ? "bg-transparent"
          : "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-[72px] gap-4">
          {/* Logo */}
          <Link href="/" to="/" className="flex items-center gap-2.5 shrink-0 group">
            <span
              className={`w-9 h-9 rounded-full flex items-center justify-center ${
                overHero ? "bg-background-50/15 border border-background-50/40" : "bg-primary-500"
              }`}
            >
              <i className={`ri-fire-fill text-lg ${overHero ? "text-accent-400" : "text-background-50"}`}></i>
            </span>
            <span
              className={`font-heading font-bold text-lg md:text-xl tracking-wide whitespace-nowrap ${
                overHero ? "text-background-50" : "text-foreground-950"
              }`}
            >
              欲望的诱惑
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const active = isActive(link.path);
              return (
                <Link
                  key={link.label}
                  href={link.path}
                  to={link.path}
                  className={`px-3 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                    active
                      ? overHero
                        ? "text-accent-400 font-medium"
                        : "text-primary-600 font-medium"
                      : overHero
                        ? "text-background-100/90 hover:text-background-50"
                        : "text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Search + mobile toggle */}
          <div className="flex items-center gap-2 md:gap-3">
            <form onSubmit={submitSearch} className="hidden md:flex items-center">
              <div
                className={`flex items-center rounded-full pl-3 pr-1 py-1 border transition-colors ${
                  overHero
                    ? "bg-background-50/15 border-background-50/40"
                    : "bg-background-100 border-background-300/70"
                }`}
              >
                <i
                  className={`ri-search-line text-base ${overHero ? "text-background-100" : "text-foreground-500"}`}
                ></i>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索影视、演员"
                  className={`w-32 xl:w-44 bg-transparent outline-none text-sm px-2 ${
                    overHero
                      ? "text-background-50 placeholder:text-background-200/80"
                      : "text-foreground-900 placeholder:text-foreground-400"
                  }`}
                />
                <button
                  type="submit"
                  className="w-7 h-7 rounded-full bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600 transition-colors cursor-pointer"
                  aria-label="搜索"
                >
                  <i className="ri-arrow-right-line text-sm"></i>
                </button>
              </div>
            </form>

            <button
              onClick={() => setMenuOpen((v) => !v)}
              className={`lg:hidden w-9 h-9 rounded-md flex items-center justify-center cursor-pointer ${
                overHero ? "text-background-50" : "text-foreground-800"
              }`}
              aria-label="菜单"
            >
              <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          menuOpen ? "max-h-[560px]" : "max-h-0"
        } ${overHero ? "bg-foreground-950/95" : "bg-background-50/98"} backdrop-blur-md border-b border-background-200/70`}
      >
        <div className="px-4 md:px-8 py-4 space-y-4">
          <form onSubmit={submitSearch} className="flex items-center">
            <div className="flex items-center w-full rounded-full pl-3 pr-1 py-1 bg-background-100 border border-background-300/70">
              <i className="ri-search-line text-base text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视、演员"
                className="flex-1 bg-transparent outline-none text-sm px-2 text-foreground-900 placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="w-7 h-7 rounded-full bg-primary-500 text-background-50 flex items-center justify-center cursor-pointer"
                aria-label="搜索"
              >
                <i className="ri-arrow-right-line text-sm"></i>
              </button>
            </div>
          </form>
          <nav className="grid grid-cols-2 gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                href={link.path}
                to={link.path}
                className={`px-3 py-2.5 text-sm rounded-md ${
                  overHero ? "text-background-100" : "text-foreground-800"
                } ${isActive(link.path) ? "text-primary-500 font-medium" : ""}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </header>
  );
}